import {
	validateAppPushConfig,
	validateInstantPushConfig,
	validateTimedPushConfig,
	validateUserInitiatedConfig
} from '@sap-px/px-config-schema-validator';

import { CentralConfig } from './CentralConfig';
import { Constants } from '../common/Constants';
import { Environment } from '../common/EnumTypes';
import { ConfigJsonParserError } from '../common/errors/ConfigJsonParserError';
import { isError } from '../common/errors/ErrorUtil';
import * as InternalTypes from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import { LoggerTexts } from '../common/LoggerTexts';
import { ConfigIdentifier, JSONValue, TenantInfo } from '../common/Types';
export class ConfigJsonParser {
	public static parseJsonToCentralConfig(
		tenantInfo: TenantInfo,
		jsonData: JSONValue
	): InternalTypes.Result<{
		centralConfig: CentralConfig;
		pushConfigStatus: InternalTypes.PushConfigStatus;
	}> {
		if (!tenantInfo) {
			return { success: false, error: new ConfigJsonParserError(undefined, LoggerTexts.CFG_JSON_PARSER.CTOR_TENANT_INFO_CANNOT_BE_NULL) };
		}
		if (!jsonData) {
			return { success: false, error: new ConfigJsonParserError(undefined, LoggerTexts.CFG_JSON_PARSER.CTOR_JSON_DATA_CANNOT_BE_NULL) };
		}
		const initConfigValidation = validateUserInitiatedConfig(jsonData);
		if (initConfigValidation.success) {
			const cfgVersion = jsonData['version'] as string;
			const supportedVersion = Constants.SUPPORTED_MAJOR_CFG_VERSION + '.';
			if (cfgVersion.startsWith(supportedVersion)) {
				const pushConfigStatus = this._getPushConfigsStatus(jsonData);
				const parsedConfig = this._parseCfgMajorVersion0(tenantInfo, jsonData) as InternalTypes.Result<CentralConfig>;
				if (isError(parsedConfig)) {
					return parsedConfig;
				}
				const result = { centralConfig: parsedConfig.result, pushConfigStatus: pushConfigStatus };
				return { success: true, result: result };
			} else {
				const errMsg = `Version of PX-Config schema is not supported. Expected major version: ${Constants.SUPPORTED_MAJOR_CFG_VERSION}. Instead received: ${cfgVersion} from server.`;
				return { success: false, error: new ConfigJsonParserError(undefined, errMsg) };
			}
		} else {
			return { success: false, error: new ConfigJsonParserError(undefined, initConfigValidation.error.message) };
		}
	}

	private static _getPushConfigsStatus(jsonData: JSONValue) {
		let appPushConfigValidationSuccess = true;
		let timedPushConfigValidationSuccess = true;
		let instantPushConfigValidationSuccess = true;
		const appPushConfigValidation = validateAppPushConfig(jsonData);
		const timedPushConfigValidation = validateTimedPushConfig(jsonData);
		const instantPushConfigValidation = validateInstantPushConfig(jsonData);
		const logger = this._getLogger();

		if (!appPushConfigValidation.success) {
			logger.error(appPushConfigValidation.error.message);
			appPushConfigValidationSuccess = false;
		}
		if (!timedPushConfigValidation.success) {
			logger.error(timedPushConfigValidation.error.message);
			timedPushConfigValidationSuccess = false;
		}
		if (!instantPushConfigValidation.success) {
			logger.error(instantPushConfigValidation.error.message);
			instantPushConfigValidationSuccess = false;
		}
		return {
			appPushConfigValidationSuccess,
			timedPushConfigValidationSuccess,
			instantPushConfigValidationSuccess
		};
	}

	private static _getLogger() {
		return new Logger(Constants.FILE_CFG_JSON_PARSER);
	}

	private static _parseCfgMajorVersion0(tenantInfo: TenantInfo, jsonData: NonNullable<JSONValue>): InternalTypes.Result<CentralConfig> {
		try {
			const version = jsonData['version'] as string;
			const unitId = jsonData['unitId'] as string;
			const envStringValue = jsonData['environment'] as string;
			const environment = Environment[envStringValue.toLowerCase()];
			const configIdentifier = { unitId: unitId, environment: environment } as ConfigIdentifier;
			const startupConfig = jsonData['startupConfig'] as InternalTypes.StartupConfig;
			const config = {
				version: version,
				startupConfig: startupConfig,
				runtimeConfig: undefined,
				pushConfig: undefined
			} as InternalTypes.PxConfig;

			if (jsonData['runtimeConfig']) {
				config.runtimeConfig = jsonData['runtimeConfig'] as InternalTypes.RuntimeConfig;
			}
			if (jsonData['pushConfig']) {
				config.pushConfig = jsonData['pushConfig'] as InternalTypes.PxPushConfig;
			}
			if (jsonData['themingConfig']) {
				config.themingConfig = jsonData['themingConfig'] as InternalTypes.ThemingConfig;
			}

			const centralConfig = new CentralConfig(configIdentifier, tenantInfo);
			centralConfig.initWithConfig(config);
			return { success: true, result: centralConfig };
		} catch (e) {
			return {
				success: false,
				error: new ConfigJsonParserError(undefined, LoggerTexts.CFG_JSON_PARSER.CTOR_JSON_DATA_UNEXPECTED_ERROR + e.message)
			};
		}
	}
}
